import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { AnimateOnScroll } from './AnimateOnScroll';

const SuccessStoriesSection = () => {
  const navigate = useNavigate();
  const { t, tm } = useLanguage();
  const [activeStory, setActiveStory] = useState(0);
  const storyCopy = tm('home.stories.items');
  const testimonialCopy = tm('home.trust.testimonials');

  const successStories = useMemo(
    () => [
      {
        id: 1,
        ...storyCopy[0],
        logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_15cb0303c-1764656703640.png',
        image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1f67694c1-1763300924452.png',
      },
      {
        id: 2,
        ...storyCopy[1],
        logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_167d7cfd4-1764659144088.png',
        image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1e67f3ed5-1766163346992.png',
      },
      {
        id: 3,
        ...storyCopy[2],
        logo: 'https://img.rocket.new/generatedImages/rocket_gen_img_14b03770f-1765038741363.png',
        image: 'https://img.rocket.new/generatedImages/rocket_gen_img_10f3511d2-1763294793864.png',
      },
    ],
    [storyCopy]
  );

  const testimonials = useMemo(
    () => [
      {
        id: 1,
        ...testimonialCopy[0],
        image: 'https://img.rocket.new/generatedImages/rocket_gen_img_124ed0d25-1763296291208.png',
      },
      {
        id: 2,
        ...testimonialCopy[1],
        image: 'https://img.rocket.new/generatedImages/rocket_gen_img_124bbe54a-1763295891998.png',
      },
      {
        id: 3,
        ...testimonialCopy[2],
        image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1ebd182ba-1763294416165.png',
      },
    ],
    [testimonialCopy]
  );

  const currentStory = successStories[activeStory];

  return (
    <section className="bg-white dark:bg-slate-950 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <AnimateOnScroll>
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-3">
              <Icon name="Star" size={15} className="text-amber-500" />
              <span className="text-xs md:text-sm font-bold tracking-widest text-amber-600 dark:text-amber-400 uppercase">
                {t('home.stories.badge') || 'CLIENT SUCCESS STORIES'}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground tracking-tight mb-3">
              {t('home.stories.title') || 'Proven Results Across Industries'}
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto font-normal">
              {t('home.stories.subtitle') || 'Discover how global organizations scale with InChi Solutions.'}
            </p>
          </div>
        </AnimateOnScroll>

        {/* Featured Case Study Card */}
        <AnimateOnScroll delay={100}>
          <div className="bg-card rounded-2xl shadow-xl overflow-hidden border border-border/80 mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative h-64 md:h-80 lg:h-full min-h-[380px] overflow-hidden">
                <Image
                  src={currentStory?.logo}
                  alt={currentStory?.logoAlt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/50 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-slate-900/80 backdrop-blur-md rounded-xl p-4 md:p-6 border border-slate-700/80">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-1">
                      {currentStory?.company}
                    </h3>
                    <p className="text-xs md:text-sm text-amber-400 font-semibold">{currentStory?.industry}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-8 lg:p-10 flex flex-col justify-between">
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon name="AlertCircle" size={18} className="text-amber-500" />
                      <h4 className="text-sm md:text-base font-bold text-foreground uppercase tracking-wider">
                        {t('home.stories.challenge')}
                      </h4>
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                      {currentStory?.challenge}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon name="Lightbulb" size={18} className="text-blue-500" />
                      <h4 className="text-sm md:text-base font-bold text-foreground uppercase tracking-wider">
                        {t('home.stories.solution')}
                      </h4>
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                      {currentStory?.solution}
                    </p>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon name="TrendingUp" size={18} className="text-emerald-500" />
                      <h4 className="text-sm md:text-base font-bold text-foreground uppercase tracking-wider">
                        {t('home.stories.result')}
                      </h4>
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground mb-4 leading-relaxed">
                      {currentStory?.result}
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      {(currentStory?.metrics || []).map((metric, index) => (
                        <div key={index} className="text-center p-3 bg-muted/40 rounded-xl border border-border/60">
                          <div className="text-lg md:text-xl font-black text-amber-500">
                            {metric?.value}
                          </div>
                          <div className="text-[11px] text-muted-foreground font-medium mt-0.5">{metric?.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-amber-500/5 rounded-xl p-4 border-l-4 border-amber-500">
                    <p className="text-xs md:text-sm text-foreground italic mb-3">
                      "{currentStory?.testimonial}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border border-amber-500/30">
                        <Image
                          src={currentStory?.image}
                          alt={currentStory?.imageAlt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="text-xs md:text-sm font-bold text-foreground">
                          {currentStory?.author}
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {currentStory?.position}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-border/80 flex items-center justify-between">
                  <div className="flex gap-2">
                    {successStories.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveStory(index)}
                        className={`h-2 rounded-full transition-all duration-300 ${
                          index === activeStory
                            ? 'bg-amber-500 w-8'
                            : 'bg-muted-foreground/30 hover:bg-muted-foreground/50 w-2'
                        }`}
                        aria-label={t('common.viewStory', { n: index + 1 })}
                      />
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/success-stories-showcase')}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="font-bold text-amber-600 dark:text-amber-400"
                  >
                    {t('home.stories.viewAll')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </AnimateOnScroll>

        {/* Client Testimonials Grid */}
        <AnimateOnScroll delay={200}>
          <div className="mb-8 text-center">
            <h3 className="text-xl md:text-2xl font-bold text-foreground">
              {t('home.trust.clientsSay') || 'What Our Enterprise Clients Say'}
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((testimonial, idx) => (
              <div
                key={testimonial.id}
                className="bg-card rounded-2xl p-6 md:p-8 border border-border/80 hover:border-amber-500/40 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between relative group"
              >
                <div className="absolute top-6 right-6 text-amber-500/20 group-hover:text-amber-500/40 transition-colors">
                  <Icon name="Quote" size={32} />
                </div>

                <div>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Icon key={i} name="Star" size={15} className="text-amber-500 fill-amber-500" />
                    ))}
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mb-6 italic leading-relaxed">
                    "{testimonial.text}"
                  </p>
                </div>

                <div className="flex items-center gap-3 pt-4 border-t border-border/60">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-amber-500/30 flex-shrink-0">
                    <Image
                      src={testimonial.image}
                      alt={testimonial.imageAlt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="text-xs md:text-sm font-bold text-foreground">
                      {testimonial.author}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-medium">
                      {testimonial.position}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default SuccessStoriesSection;
